import { BatteryPricingMethodOption, PriceSheetMappingDocument, ProgramType } from 'types';
import type { Clientize, MonthlyMultipliers, PricingPlanDocument } from 'types';

import { api } from './api';
import { createSSECacheHandler } from './createSSECacheHandler';

/** Mirrors the `PaginatedPriceSheets` response schema in packages/api/src/schemas/priceSheets.ts. */
interface PaginatedPriceSheets {
  total: number;
  pageNum: number;
  data: { id: string; name: string }[];
}

export interface ProductPriceCap {
  id: string;
  manufacturer: string;
  model: string;
  maxPrice?: number;
}

/** ObjectIds and Dates arrive over the wire as strings. */
export type SeasonalPricingPlan = Clientize<PricingPlanDocument>;

/** The version detail response pairs the live version with its superseded ones. */
export interface SeasonalPricingPlanDetail {
  current: SeasonalPricingPlan;
  history: SeasonalPricingPlan[];
}

interface SeasonalPricingPlanVersionBody {
  name: string;
  monthlyMultipliers: MonthlyMultipliers;
  adminFeeMonthly?: number;
  acknowledgeException?: boolean;
}

type CreateSeasonalPricingPlanBody = SeasonalPricingPlanVersionBody & { programType: string };

const buildQueryString = (params?: string[]): Record<string, string> => {
  if (!params || params.length === 0) {
    return {};
  }

  return params.reduce((acc: Record<string, string>, param: string) => {
    acc[param] = 'true';
    return acc;
  }, {});
};

export const pricingApi = api.injectEndpoints({
  endpoints: (build) => ({
    createConstructionAdder: build.mutation<any, { data: any; programType: ProgramType }>({
      query: ({ data, programType }) => ({
        url: `/admin/${programType}/construction-adders`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { data }) => [
        { type: 'ConstructionAdders', id: data.adderType },
        { type: 'History', id: 'LIST' },
      ],
    }),
    createWarrantyPricing: build.mutation<any, { data: any; programType: ProgramType }>({
      query: ({ data, programType }) => ({
        url: `/admin/${programType}/warranties`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { data }) => [
        { type: 'Warranties', id: data.warrantyType },
        { type: 'History', id: 'LIST' },
      ],
    }),
    archiveWarrantyPricing: build.mutation<any, { id: string }>({
      query: ({ id }) => ({ url: `/admin/warranties/${id}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Warranties', id },
        { type: 'History', id: 'LIST' },
      ],
    }),
    editConstructionAdder: build.mutation<any, { id: string; data: any }>({
      query: ({ id, data }) => ({ url: `/admin/construction-adders/${id}`, method: 'PATCH', body: data }),
      invalidatesTags: (_result, _error, { data }) => [
        { type: 'ConstructionAdders', id: data.adderType },
        { type: 'History', id: 'LIST' },
      ],
    }),
    createHoldback: build.mutation<any, { data: any }>({
      query: ({ data }) => ({ url: `/admin/holdbacks`, method: 'POST', body: data }),
      invalidatesTags: () => [
        { type: 'Holdbacks', id: 'LIST' },
        { type: 'History', id: 'LIST' },
      ],
    }),
    editHoldback: build.mutation<any, { id: string; data: any }>({
      query: ({ id, data }) => ({ url: `/admin/holdbacks/${id}`, method: 'PATCH', body: data }),
      invalidatesTags: () => [
        { type: 'Holdbacks', id: 'LIST' },
        { type: 'History', id: 'LIST' },
      ],
    }),
    createModifier: build.mutation<any, { data: any }>({
      query: ({ data }) => ({ url: `/admin/modifiers`, method: 'POST', body: data }),
      invalidatesTags: (_result, _error, { data }) => [
        { type: 'Modifiers', id: data.modifierType },
        { type: 'History', id: 'LIST' },
      ],
    }),
    createItcModifier: build.mutation<any, { data: any }>({
      query: ({ data }) => ({ url: `/admin/itc-modifiers`, method: 'POST', body: data }),
      invalidatesTags: (_result, _error, { data }) => [
        { type: 'Modifiers', id: data.modifierType },
        { type: 'History', id: 'LIST' },
      ],
    }),
    editModifier: build.mutation<any, { id: string; data: any }>({
      query: ({ id, data }) => ({ url: `/admin/modifiers/${id}`, method: 'PATCH', body: data }),
      invalidatesTags: (_result, _error, { data }) => [
        { type: 'Modifiers', id: data.modifierType },
        { type: 'History', id: 'LIST' },
      ],
    }),
    editItcModifier: build.mutation<any, { id: string; data: any }>({
      query: ({ id, data }) => ({ url: `/admin/itc-modifiers/${id}`, method: 'PATCH', body: data }),
      invalidatesTags: (_result, _error, { data }) => [
        { type: 'Modifiers', id: data.modifierType },
        { type: 'History', id: 'LIST' },
      ],
    }),
    createPriceSheetMapping: build.mutation<any, { data: any; programType: ProgramType }>({
      query: ({ data, programType }) => ({
        url: `/admin/${programType}/price-sheets/mappings`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: () => [
        { type: 'PriceSheetMappings', id: 'LIST' },
        { type: 'History', id: 'LIST' },
      ],
    }),
    archivePriceSheetMapping: build.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `/admin/price-sheets/mappings/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => [
        { type: 'PriceSheetMappings', id: 'LIST' },
        { type: 'History', id: 'LIST' },
      ],
    }),
    applyBulkPriceSheetMappings: build.mutation<any, { data: any; programType: ProgramType }>({
      query: ({ data, programType }) => ({
        url: `/admin/${programType}/price-sheets/bulk-mappings`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: () => [
        { type: 'PriceSheetMappings', id: 'LIST' },
        { type: 'History', id: 'LIST' },
      ],
    }),
    /**
     * Async bulk price-sheet-mapping update.
     *
     * Returns immediately with a `jobId` while the actual archive+create
     * happens in the worker queue. Pair with
     * `useGetPriceSheetMappingBulkJobQuery` (with `pollingInterval`) to
     * track progress. Avoids the multi-minute synchronous request that
     * was a contributor to the 2026-05-04 sabal-prod OOM incident.
     */
    enqueuePriceSheetMappingBulkJob: build.mutation<
      { jobId: string; total: number; status: string },
      { data: any; programType: ProgramType }
    >({
      query: ({ data, programType }) => ({
        url: `/admin/${programType}/price-sheets/bulk-mappings`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: () => [{ type: 'PriceSheetMappingBulkJob', id: 'LIST' }],
    }),
    getPriceSheetMappingBulkJob: build.query<
      {
        jobId: string;
        status: 'pending' | 'processing' | 'completed' | 'failed';
        programType: ProgramType;
        total: number;
        processed: number;
        error?: string;
        createdAt?: string;
        startedAt?: string;
        completedAt?: string;
      },
      { jobId: string }
    >({
      query: ({ jobId }) => ({ url: `/admin/price-sheets/bulk-mappings/${jobId}` }),
      providesTags: (_result, _error, { jobId }) => [{ type: 'PriceSheetMappingBulkJob', id: jobId }],
      onCacheEntryAdded: createSSECacheHandler<
        {
          jobId: string;
          status: 'pending' | 'processing' | 'completed' | 'failed';
          programType: ProgramType;
          total: number;
          processed: number;
          error?: string;
          createdAt?: string;
          startedAt?: string;
          completedAt?: string;
        },
        { status: 'pending' | 'processing' | 'completed' | 'failed'; processed: number; error?: string }
      >({
        getSSEUrl: (data) =>
          data.status === 'pending' || data.status === 'processing'
            ? `/api/admin/price-sheets/bulk-mappings/${data.jobId}/progress`
            : null,
        updateCache: (draft, message) => {
          draft.status = message.status;
          draft.processed = message.processed;
          if (message.error !== undefined) draft.error = message.error;
        },
      }),
    }),
    getPriceSheetMappingBulkJobs: build.query<
      {
        data: {
          jobId: string;
          status: 'pending' | 'processing' | 'completed' | 'failed';
          programType: ProgramType;
          total: number;
          processed: number;
          error?: string;
          requestedBy?: string;
          createdAt?: string;
          startedAt?: string;
          completedAt?: string;
        }[];
        total: number;
        pageNum: number;
        pageSize: number;
      },
      { programType: ProgramType | string; pageNum?: number; pageSize?: number }
    >({
      query: ({ programType, pageNum, pageSize }) => {
        const params = new URLSearchParams();
        if (pageNum !== undefined) params.set('pageNum', String(pageNum));
        if (pageSize !== undefined) params.set('pageSize', String(pageSize));
        const qs = params.toString();
        return { url: `/admin/${programType}/price-sheets/bulk-mappings${qs ? `?${qs}` : ''}` };
      },
      providesTags: (result) =>
        result
          ? [
              { type: 'PriceSheetMappingBulkJob', id: 'LIST' },
              ...result.data.map(({ jobId }) => ({ type: 'PriceSheetMappingBulkJob' as const, id: jobId })),
            ]
          : [{ type: 'PriceSheetMappingBulkJob', id: 'LIST' }],
    }),
    getPriceSheetMappingById: build.query<PriceSheetMappingDocument<ProgramType>, { id: string }>({
      query: ({ id }) => ({ url: `/admin/price-sheets/mappings/${id}` }),
      providesTags: (_result, _error, { id }) => [{ type: 'PriceSheetMappings', id }],
    }),
    getConstructionAdders: build.query<any, { type: string; programType: string }>({
      query: ({ type, programType }) => ({
        url: `/admin/${programType}/construction-adders?${new URLSearchParams({ adderType: type })}`,
      }),
      providesTags: (_result, _error, { type }) => [{ type: 'ConstructionAdders', id: type }],
    }),
    getWarranties: build.query<any, { type: string; programType: string }>({
      query: ({ type, programType }) => ({
        url: `/admin/${programType}/warranties?${new URLSearchParams({ type })}`,
      }),
      providesTags: (result, _error, { type }) =>
        result
          ? [...result.map(({ id }: any) => ({ type: 'Warranties' as const, id })), { type: 'Warranties', id: type }]
          : [{ type: 'Warranties', id: type }],
    }),
    getHoldbacks: build.query<any, { programType: string }>({
      query: ({ programType }) => ({ url: `/admin/${programType}/holdbacks` }),
      providesTags: () => [{ type: 'Holdbacks', id: 'LIST' }],
    }),
    getOrganizationPricingHoldbacks: build.query<any, { alias: string; programType: string }>({
      query: ({ alias, programType }) => ({
        url: `/organizations/${alias}/pricing/holdbacks`,
        params: { programType },
      }),
      providesTags: (_result, _error, { alias, programType }) => [
        { type: 'OrganizationHoldbacks', id: `${alias}-${programType}` },
      ],
    }),
    getOrganizationPricingHoldbackById: build.query<any, { alias: string; holdbackId: string }>({
      query: ({ alias, holdbackId }) => ({
        url: `/organizations/${alias}/pricing/holdbacks/${holdbackId}`,
      }),
      providesTags: (_result, _error, { alias, holdbackId }) => [
        { type: 'OrganizationHoldbacks', id: `${alias}-${holdbackId}` },
      ],
    }),
    createOrganizationPricingHoldbacks: build.mutation<any, { alias: string; data: any }>({
      query: ({ alias, data }) => ({ url: `/organizations/${alias}/pricing/holdbacks`, method: 'POST', body: data }),
      invalidatesTags: (_result, _error, { alias, data }) => [
        { type: 'OrganizationHoldbacks', id: `${alias}-${data.programType}` },
      ],
    }),
    deleteOrganizationPricingHoldbacks: build.mutation<any, { alias: string; id: string; programType: string }>({
      query: ({ alias, id }) => ({ url: `/organizations/${alias}/pricing/holdbacks/${id}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, { alias, programType }) => [
        { type: 'OrganizationHoldbacks', id: `${alias}-${programType}` },
      ],
    }),
    createOrganizationEnergyCommunity: build.mutation<any, { alias: string; data: any }>({
      query: ({ alias, data }) => ({
        url: `/organizations/${alias}/pricing/energy-community`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { alias, data }) => [
        { type: 'OrganizationEnergyCommunity', id: `${alias}-${data.programType}` },
      ],
    }),
    deleteOrganizationEnergyCommunity: build.mutation<any, { alias: string; id: string; programType: string }>({
      query: ({ alias, id }) => ({
        url: `/organizations/${alias}/pricing/energy-community/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { alias, programType }) => [
        { type: 'OrganizationEnergyCommunity', id: `${alias}-${programType}` },
      ],
    }),
    getOrganizationEnergyCommunity: build.query<any, { alias: string; programType: string }>({
      query: ({ alias, programType }) => ({
        url: `/organizations/${alias}/pricing/energy-community`,
        params: { programType },
      }),
      providesTags: (_result, _error, { alias, programType }) => [
        { type: 'OrganizationEnergyCommunity', id: `${alias}-${programType}` },
      ],
    }),
    createOrganizationPpwModifiers: build.mutation<any, { alias: string; data: any }>({
      query: ({ alias, data }) => ({
        url: `/organizations/${alias}/pricing/ppw-modifiers`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { alias, data }) => [
        { type: 'OrganizationPpwModifiers', id: `${alias}-${data.programType}` },
      ],
    }),
    deleteOrganizationPpwModifier: build.mutation<any, { alias: string; id: string; programType: string }>({
      query: ({ alias, id }) => ({
        url: `/organizations/${alias}/pricing/ppw-modifiers/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { alias, programType }) => [
        { type: 'OrganizationPpwModifiers', id: `${alias}-${programType}` },
      ],
    }),
    getOrganizationPpwModifiers: build.query<any, { alias: string; programType: string }>({
      query: ({ alias, programType }) => ({
        url: `/organizations/${alias}/pricing/ppw-modifiers`,
        params: { programType },
      }),
      providesTags: (_result, _error, { alias, programType }) => [
        { type: 'OrganizationPpwModifiers', id: `${alias}-${programType}` },
      ],
    }),
    createOrganizationDomesticContent: build.mutation<any, { alias: string; data: any }>({
      query: ({ alias, data }) => ({
        url: `/organizations/${alias}/pricing/domestic-content`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { alias, data }) => [
        { type: 'OrganizationDomesticContent', id: `${alias}-${data.programType}` },
      ],
    }),
    getOrganizationDomesticContent: build.query<any, { alias: string; programType: string }>({
      query: ({ alias, programType }) => ({
        url: `/organizations/${alias}/pricing/domestic-content`,
        params: { programType },
      }),
      providesTags: (_result, _error, { alias, programType }) => [
        { type: 'OrganizationDomesticContent', id: `${alias}-${programType}` },
      ],
    }),
    getOrganizationDomesticContentById: build.query<any, { alias: string; domesticContentModifierId: string }>({
      query: ({ alias, domesticContentModifierId }) => ({
        url: `/organizations/${alias}/pricing/domestic-content/${domesticContentModifierId}`,
      }),
      providesTags: (_result, _error, { alias, domesticContentModifierId }) => [
        { type: 'OrganizationDomesticContent', id: `${alias}-${domesticContentModifierId}` },
      ],
    }),
    deleteOrganizationDomesticContent: build.mutation<any, { alias: string; id: string; programType: string }>({
      query: ({ alias, id }) => ({
        url: `/organizations/${alias}/pricing/domestic-content/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { alias, programType }) => [
        { type: 'OrganizationDomesticContent', id: `${alias}-${programType}` },
      ],
    }),
    getOrganizationSiteSurveyPricing: build.query<{ data: any[] }, { alias: string; programType: string }>({
      query: ({ alias, programType }) => ({
        url: `/organizations/${alias}/site-survey-pricing`,
        params: { programType },
      }),
      providesTags: (_result, _error, { alias, programType }) => [
        { type: 'OrganizationSiteSurveyPricing', id: `${alias}-${programType}` },
      ],
    }),
    createOrganizationSiteSurveyPricing: build.mutation<
      any,
      { alias: string; data: { amount: number; state: string; programType: string } }
    >({
      query: ({ alias, data }) => ({
        url: `/organizations/${alias}/site-survey-pricing`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { alias, data }) => [
        { type: 'OrganizationSiteSurveyPricing', id: `${alias}-${data.programType}` },
      ],
    }),
    deleteOrganizationSiteSurveyPricing: build.mutation<any, { alias: string; id: string; programType: string }>({
      query: ({ alias, id }) => ({
        url: `/organizations/${alias}/site-survey-pricing/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { alias, programType }) => [
        { type: 'OrganizationSiteSurveyPricing', id: `${alias}-${programType}` },
      ],
    }),
    getOrganizationPropertyTypePricing: build.query<{ data: any[] }, { alias: string; programType: string }>({
      query: ({ alias, programType }) => ({
        url: `/organizations/${alias}/property-type-pricing`,
        params: { programType },
      }),
      providesTags: (_result, _error, { alias, programType }) => [
        { type: 'OrganizationPropertyTypePricing', id: `${alias}-${programType}` },
      ],
    }),
    createOrganizationPropertyTypePricing: build.mutation<
      any,
      { alias: string; data: { pricePerWatt: number; propertyType: string; programType: string } }
    >({
      query: ({ alias, data }) => ({
        url: `/organizations/${alias}/property-type-pricing`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { alias, data }) => [
        { type: 'OrganizationPropertyTypePricing', id: `${alias}-${data.programType}` },
      ],
    }),
    deleteOrganizationPropertyTypePricing: build.mutation<any, { alias: string; id: string; programType: string }>({
      query: ({ alias, id }) => ({
        url: `/organizations/${alias}/property-type-pricing/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { alias, programType }) => [
        { type: 'OrganizationPropertyTypePricing', id: `${alias}-${programType}` },
      ],
    }),
    getModifiers: build.query<any, { programType: string; type: string }>({
      query: ({ programType, type }) => ({
        url: `/admin/${programType}/modifiers?${new URLSearchParams({ modifierType: type })}`,
      }),
      providesTags: (_result, _error, { type }) => [{ type: 'Modifiers', id: type }],
    }),
    getItcModifiers: build.query<any, { programType: string; type: string }>({
      query: ({ programType, type }) => ({
        url: `/admin/${programType}/itc-modifiers?${new URLSearchParams({ modifierType: type })}`,
      }),
      providesTags: (_result, _error, { type }) => [{ type: 'Modifiers', id: type }],
    }),
    getPriceSheets: build.query<PaginatedPriceSheets, Record<any, any>>({
      query: ({ programType, ...queryParams }) => {
        return {
          url: `admin/${programType}/price-sheets${
            queryParams &&
            '?' +
              new URLSearchParams({
                ...queryParams,
              }).toString()
          }`,
        };
      },
      providesTags: () => [{ type: 'PriceSheets', id: 'LIST' }],
    }),
    uploadPriceSheet: build.mutation<any, { formData: FormData; programType: ProgramType }>({
      query: ({ formData, programType }) => ({
        url: `admin/${programType}/price-sheets`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: () => [
        { type: 'PriceSheets', id: 'LIST' },
        { type: 'History', id: 'LIST' },
      ],
    }),
    getPriceSheetDetails: build.query<any, { id: string }>({
      query: ({ id }) => ({ url: `/admin/price-sheets/${id}` }),
      providesTags: (_result, _error, { id }) => [{ type: 'PriceSheets', id }],
    }),
    editPriceSheet: build.mutation<any, { id: string; data: any }>({
      query: ({ id, data }) => ({ url: `/admin/price-sheets/${id}`, method: 'PATCH', body: data }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'PriceSheets', id: 'LIST' },
        { type: 'PriceSheets', id },
        { type: 'History', id: 'LIST' },
      ],
    }),
    getPriceSheetMappingsById: build.query<any, { id: string }>({
      query: ({ id }) => ({ url: `/admin/price-sheets/${id}/mappings` }),
    }),
    getPricing: build.query<
      any[],
      {
        id: string;
        batteryType?: string;
        batteryPricingMethod?: string;
        batteryCost?: number;
        electricalUpgradeIncluded?: boolean;
        includeDomesticContentPricing?: boolean;
      }
    >({
      query: ({
        id,
        batteryCost,
        includeDomesticContentPricing,
        batteryType,
        batteryPricingMethod,
        electricalUpgradeIncluded,
      }) => {
        const urlSearchParams = new URLSearchParams();
        if (batteryType !== undefined) {
          urlSearchParams.append('batteryType', batteryType);
        }
        if (batteryPricingMethod !== undefined) {
          urlSearchParams.append('batteryPricingMethod', batteryPricingMethod);
        }
        if (batteryCost !== undefined) {
          urlSearchParams.append('batteryCost', `${batteryCost}`);
        }
        if (includeDomesticContentPricing !== undefined) {
          urlSearchParams.append('includeDomesticContentPricing', `${includeDomesticContentPricing}`);
        }
        if (electricalUpgradeIncluded !== undefined) {
          urlSearchParams.append('electricalUpgradeIncluded', `${electricalUpgradeIncluded}`);
        }
        return {
          url: `/v3/accounts/${id}/pricing?${urlSearchParams}`,
        };
      },
      providesTags: (_result, _error, { id }) => [{ type: 'Pricing', id }],
    }),
    getHVACPricing: build.query<any[], { accountId: string; totalFinancedAmount: number }>({
      query: ({ accountId, totalFinancedAmount }) => ({
        url: `/v2/accounts/${accountId}/pricing/hvac?totalFinancedAmount=${totalFinancedAmount || 0}`,
      }),
    }),
    fetchHVACAccountPricing: build.mutation<any, any>({
      query: (body) => ({
        url: `/v2/accounts/${body.accountId}/pricing/hvac`,
        method: 'POST',
        body,
      }),
    }),
    fetchEnergyBackupAccountPricing: build.mutation<any, any>({
      query: (body) => ({
        url: `/v2/accounts/${body.accountId}/pricing/energy-backup`,
        method: 'POST',
        body,
      }),
    }),
    fetchWaterHeaterAccountPricing: build.mutation<any, any>({
      query: (body) => ({
        url: `/v2/accounts/${body.accountId}/pricing/water-heater`,
        method: 'POST',
        body,
      }),
    }),
    fetchGeneratorAccountPricing: build.mutation<any, any>({
      query: (body) => ({
        url: `/v2/accounts/${body.accountId}/pricing/generator`,
        method: 'POST',
        body,
      }),
    }),
    fetchGeothermalAccountPricing: build.mutation<any, any>({
      query: (body) => ({
        url: `/v2/accounts/${body.accountId}/pricing/geothermal`,
        method: 'POST',
        body,
      }),
    }),
    postEstimatedPricing: build.mutation<
      any[],
      {
        accountId?: string;
        lseId: number;
        state: string;
        systemSizeKw: number;
        systemFirstYearProductionKwh: number;
        battery?: {
          type: 'backupBattery' | 'arbitrageBattery';
          pricingMethod: BatteryPricingMethodOption;
          cost: number;
        };
        electricalUpgradeIncluded?: boolean;
        includeDomesticContentPricing?: boolean;
        organizationId?: string;
        programType?: ProgramType;
      }
    >({
      query: (body) => ({
        url: `/v3/estimated-pricing`,
        method: 'POST',
        body,
      }),
    }),
    getDOEPRPricing: build.query<
      any[],
      {
        id: string;
        adders?: string[];
        backupBatteryManufacturer?: number;
        backupBatteryModelNumber?: string;
        backupBatteryQuantity?: number;
        solarReadinessCost?: number;
      }
    >({
      query: ({
        id,
        adders,
        backupBatteryManufacturer,
        backupBatteryModelNumber,
        backupBatteryQuantity,
        solarReadinessCost,
      }) => {
        const urlSearchParams = new URLSearchParams();
        if (backupBatteryManufacturer !== undefined) {
          urlSearchParams.append('backupBatteryManufacturer', `${backupBatteryManufacturer}`);
        }
        if (backupBatteryModelNumber !== undefined) {
          urlSearchParams.append('backupBatteryModelNumber', `${backupBatteryModelNumber}`);
        }
        if (backupBatteryQuantity !== undefined) {
          urlSearchParams.append('backupBatteryQuantity', `${backupBatteryQuantity}`);
        }
        if (solarReadinessCost !== undefined) {
          urlSearchParams.append('solarReadinessCost', `${solarReadinessCost}`);
        }
        return {
          url: `/v2/accounts/${id}/pricing/doe-pr?${adders?.length ? new URLSearchParams(buildQueryString(adders)) : ''}${adders?.length ? '&' : ''}${`${urlSearchParams}`}`,
        };
      },
      providesTags: (_result, _error, { id }) => [{ type: 'Pricing', id }],
    }),
    getPriceSheetMappings: build.query<
      {
        data: any[];
        total: number;
        pageNum: number;
        pageSize: number;
      },
      {
        programType: ProgramType | string;
        pageNum?: number;
        pageSize?: number;
        searchTerm?: string;
        advancedFilters?: string;
      }
    >({
      query: ({ programType, pageNum, pageSize, searchTerm, advancedFilters }) => {
        const params = new URLSearchParams();
        if (pageNum !== undefined) params.set('pageNum', String(pageNum));
        if (pageSize !== undefined) params.set('pageSize', String(pageSize));
        if (searchTerm) params.set('searchTerm', searchTerm);
        if (advancedFilters) params.set('advancedFilters', advancedFilters);
        const qs = params.toString();
        return {
          url: `/admin/${programType}/price-sheets/mappings${qs ? `?${qs}` : ''}`,
        };
      },
      providesTags: () => [{ type: 'PriceSheetMappings', id: 'LIST' }],
    }),
    getProductPriceCaps: build.query<ProductPriceCap[], { accountId: string; productType: string }>({
      query: ({ accountId, productType }) => ({ url: `/accounts/${accountId}/pricing/price-caps/${productType}` }),
    }),
    getPriceSheetMappingsListFilters: build.query<any, { programType: ProgramType; includeOptions?: boolean }>({
      query: ({ programType, includeOptions }) => {
        const params = new URLSearchParams();
        if (includeOptions === false) {
          params.set('includeOptions', 'false');
        }
        const queryString = params.toString();
        return {
          url: `/admin/${programType}/price-sheets/mappings/filters${queryString ? `?${queryString}` : ''}`,
        };
      },
    }),
    getPriceSheetMappingsListFilter: build.query<any, { programType: ProgramType; id: string }>({
      query: ({ programType, id }) => ({
        url: `/admin/${programType}/price-sheets/mappings/filters/${id}`,
      }),
    }),
    listSeasonalPricingPlans: build.query<SeasonalPricingPlan[], { programType: string }>({
      query: ({ programType }) => ({
        url: `/admin/seasonal-pricing-plans?${new URLSearchParams({ programType })}`,
      }),
      providesTags: (result) =>
        result
          ? [
              { type: 'SeasonalPricingPlans', id: 'LIST' },
              ...result.map(({ planGroupId }) => ({ type: 'SeasonalPricingPlans' as const, id: planGroupId })),
            ]
          : [{ type: 'SeasonalPricingPlans', id: 'LIST' }],
    }),
    getSeasonalPricingPlan: build.query<SeasonalPricingPlanDetail, { planGroupId: string }>({
      query: ({ planGroupId }) => ({ url: `/admin/seasonal-pricing-plans/${planGroupId}` }),
      providesTags: (_result, _error, { planGroupId }) => [{ type: 'SeasonalPricingPlans', id: planGroupId }],
    }),
    createSeasonalPricingPlan: build.mutation<SeasonalPricingPlan, CreateSeasonalPricingPlanBody>({
      query: (body) => ({ url: `/admin/seasonal-pricing-plans`, method: 'POST', body }),
      invalidatesTags: () => [{ type: 'SeasonalPricingPlans', id: 'LIST' }],
    }),
    createSeasonalPricingPlanVersion: build.mutation<
      SeasonalPricingPlan,
      { planGroupId: string } & SeasonalPricingPlanVersionBody
    >({
      query: ({ planGroupId, ...body }) => ({
        url: `/admin/seasonal-pricing-plans/${planGroupId}/versions`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { planGroupId }) => [
        { type: 'SeasonalPricingPlans', id: 'LIST' },
        { type: 'SeasonalPricingPlans', id: planGroupId },
      ],
    }),
    archiveSeasonalPricingPlan: build.mutation<SeasonalPricingPlan, { planGroupId: string }>({
      query: ({ planGroupId }) => ({
        url: `/admin/seasonal-pricing-plans/${planGroupId}/archive`,
        method: 'PATCH',
      }),
      invalidatesTags: (_result, _error, { planGroupId }) => [
        { type: 'SeasonalPricingPlans', id: 'LIST' },
        { type: 'SeasonalPricingPlans', id: planGroupId },
      ],
    }),
  }),
});

export const {
  useCreateConstructionAdderMutation,
  useCreateHoldbackMutation,
  useCreateModifierMutation,
  useCreateItcModifierMutation,
  useCreateOrganizationEnergyCommunityMutation,
  useCreateOrganizationPpwModifiersMutation,
  useCreateOrganizationDomesticContentMutation,
  useCreateOrganizationPricingHoldbacksMutation,
  useCreatePriceSheetMappingMutation,
  useDeleteOrganizationEnergyCommunityMutation,
  useDeleteOrganizationPpwModifierMutation,
  useDeleteOrganizationDomesticContentMutation,
  useDeleteOrganizationPricingHoldbacksMutation,
  useEditConstructionAdderMutation,
  useEditHoldbackMutation,
  useEditModifierMutation,
  useEditItcModifierMutation,
  useEditPriceSheetMutation,
  useGetConstructionAddersQuery,
  useGetHoldbacksQuery,
  useGetModifiersQuery,
  useGetItcModifiersQuery,
  useGetOrganizationEnergyCommunityQuery,
  useGetOrganizationDomesticContentQuery,
  useGetOrganizationDomesticContentByIdQuery,
  useGetOrganizationPricingHoldbacksQuery,
  useGetOrganizationPricingHoldbackByIdQuery,
  useGetPriceSheetDetailsQuery,
  useGetPriceSheetMappingByIdQuery,
  useGetPriceSheetMappingsByIdQuery,
  useGetPriceSheetMappingsListFiltersQuery,
  useGetPriceSheetMappingsListFilterQuery,
  useGetPriceSheetMappingsQuery,
  useGetPriceSheetsQuery,
  useGetPricingQuery,
  useGetProductPriceCapsQuery,
  useLazyGetConstructionAddersQuery,
  useLazyGetHoldbacksQuery,
  useLazyGetModifiersQuery,
  useLazyGetItcModifiersQuery,
  useLazyGetOrganizationEnergyCommunityQuery,
  useLazyGetOrganizationPpwModifiersQuery,
  useLazyGetOrganizationDomesticContentQuery,
  useLazyGetOrganizationPricingHoldbacksQuery,
  useLazyGetPriceSheetDetailsQuery,
  useLazyGetPriceSheetMappingsQuery,
  useLazyGetPriceSheetsQuery,
  useUploadPriceSheetMutation,
  useArchivePriceSheetMappingMutation,
  useApplyBulkPriceSheetMappingsMutation,
  useEnqueuePriceSheetMappingBulkJobMutation,
  useGetPriceSheetMappingBulkJobQuery,
  useGetPriceSheetMappingBulkJobsQuery,
  useLazyGetPriceSheetMappingBulkJobQuery,
  useGetDOEPRPricingQuery,
  useLazyGetHVACPricingQuery,
  useGetWarrantiesQuery,
  useCreateWarrantyPricingMutation,
  useArchiveWarrantyPricingMutation,
  useFetchHVACAccountPricingMutation,
  useFetchEnergyBackupAccountPricingMutation,
  useFetchWaterHeaterAccountPricingMutation,
  useFetchGeneratorAccountPricingMutation,
  useFetchGeothermalAccountPricingMutation,
  usePostEstimatedPricingMutation,
  useGetOrganizationPpwModifiersQuery,
  useGetOrganizationSiteSurveyPricingQuery,
  useCreateOrganizationSiteSurveyPricingMutation,
  useDeleteOrganizationSiteSurveyPricingMutation,
  useGetOrganizationPropertyTypePricingQuery,
  useCreateOrganizationPropertyTypePricingMutation,
  useDeleteOrganizationPropertyTypePricingMutation,
  useListSeasonalPricingPlansQuery,
  useGetSeasonalPricingPlanQuery,
  useCreateSeasonalPricingPlanMutation,
  useCreateSeasonalPricingPlanVersionMutation,
  useArchiveSeasonalPricingPlanMutation,
} = pricingApi;
